import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { useGetDailyMetricsSummaryExportQuery } from '../../pages/metricdashboard/metricDashboardSlice';
import { monthNames } from '../../utils/typeConstants';
// Component to export daily metrics

type ExportDailyMetricProps = {
    inputmonth: number;
    inputyear: number;
    dailyMetricsDataLength: number;
    agentDataValueLength: number;
    metricStatusDataFilterLength: number;
    agentnumberValue: string;
    scorecardVersion: number;
    FullEmployeeName:string;
}

export const ExportDailyMetric = ({ inputmonth, inputyear, dailyMetricsDataLength, agentDataValueLength, metricStatusDataFilterLength, agentnumberValue, scorecardVersion, FullEmployeeName }:ExportDailyMetricProps) => {
    // State variables for export data and full employee name
    const [exportData, setExportData] = useState<any>([]);
    // Fetch daily metrics data using custom hook
    const { data: dailyMetricsExportData } = useGetDailyMetricsSummaryExportQuery(
        {
            employeenumber: parseInt(agentnumberValue, 10),
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: 1,
        },
        {
            skip:
                !agentnumberValue ||
                !scorecardVersion||
                dailyMetricsDataLength === 0 ||
                agentDataValueLength === 0 ||
                metricStatusDataFilterLength === 0,
        }
    );
    // Update export data when daily metrics data changes
    useEffect(() => {
        if (dailyMetricsExportData?.data.length > 0) {
            setExportData(dailyMetricsExportData?.data.length > 0 ? dailyMetricsExportData?.data : '');
        }
    }, [dailyMetricsExportData]);
    
    // Function to download excel file
    const downloadExcel = () => {
        import('xlsx').then((XLSX: any) => {
            // Prepare data for excel
            const lookup: any = {};
            for (let k = 0, i = 1; k < exportData.length; k++) {
                const name = exportData[k].metricname;
                if (!(name in lookup)) {
                    lookup[name] = i++;
                }
            }
            // Map data to required format
            const prep = exportData.map((pro: any) => {
                return {
                    Date: pro.date,
                    name: pro.metricname,
                    Target: pro.agentmetrictarget,
                    Actual: pro.metricmeasurementforday,
                    Attainment: pro.metricattainmentindicator,
                    SupervisorID: pro.supervisorID,
                    SupervisorName: pro.supervisorName,
                    id: lookup[pro.metricname],
                };
            });
            // Sort data by date
            prep.sort((a: any, b: any) => a.date - b.date);
            // Prepare headers for excel
            const headers = ['Date', 'Supervisor ID', 'Supervisor Name'].concat(
                [...Array.from(new Set(prep.map((obj: any) => obj.id)))]
                    .map((id) => [`Metric${id} Name`, `Agent Target${id}`, `Actual${id}`, `Attainment Y/N`])
                    .flat()
            );
            // Prepare data for excel
            const wsdata: any = [...Array.from(new Set(prep.map((obj: any) => obj.Date)))].map((ts: any) => {
                const objByTimestamp = prep.filter((obj: any) => obj.Date === ts);
                let arr = [ts];
                if (objByTimestamp.length > 0) {
                    arr = arr.concat(objByTimestamp[0].SupervisorID, objByTimestamp[0].SupervisorName);
                }
                objByTimestamp.forEach((obj: any) => {
                    arr = arr.concat([obj.name, obj.Target, obj.Actual, obj.Attainment]);
                });
                return arr;
            });
            // Add headers to data
            wsdata.unshift(headers);
            // Create new workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(wsdata, { origin: 'A5' });
            // Fit columns to data
            ws['!cols'] = fitToColumn(wsdata);
            function fitToColumn(wsdata: any) {
                return wsdata[0].map((a: any, i: any) => ({
                    wch: Math.max(...wsdata.map((a2: any) => (a2[i] ? a2[i].toString().length : 0))),
                }));
            }
            // Add additional data to worksheet
            XLSX.utils.sheet_add_aoa(
                ws,
                [[FullEmployeeName], [`Month: ${monthNames[(inputmonth)]}, ${inputyear}`]],
                { origin: 'A2' }
            );
            // Add title to worksheet
            XLSX.utils.sheet_add_aoa(ws, [['P4P Daily Metric Summary']], { origin: 'D1' });
            // Append worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            // Write workbook to buffer
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            // Save buffer as excel file
            saveAsExcelFile(excelBuffer, 'agents');
        });
    };
    // Function to save buffer as excel file
    const saveAsExcelFile = (buffer: any, fileName: any) => {
        import('file-saver').then((module) => {
            if (module && module.default) {
                const EXCEL_TYPE =
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                const EXCEL_EXTENSION = '.xlsx';
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE,
                });
                module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };
    // Render excel export button
    return (
        <Button
            type="button"
            icon="pi pi-file-excel me-2"
            onClick={downloadExcel}
            className="btn-export me-4 mt-4 float-end"
            data-pr-tooltip="XLS"
        >
            Export as Excel
        </Button>
    );
};
